import { MINUTES_PER_DAY, ResizeEdge, type LiveTimeBlock } from '@wiztime/core';
import { blockHeightPx, MIN_TEXT_BLOCK_PX, type DisplayGroup } from './laneModel';

/**
 * Pure hit tests of a block lane (ui-redesign §3.2.2): which edge of which block a mouse press
 * lands on. The lane's measured box is passed in as a `LaneGeometry`, so none of these functions
 * reads the DOM.
 */

/** Outer resize zone beside the edges of a block too short for inner edge bands (§3.2.2). */
export const OUTER_RESIZE_ZONE_PX = 4;
/** An inner edge band is at most 8px and at least 4px (25% of the height in between, §3.2.2). */
export const EDGE_BAND_MAX_PX = 8;
export const EDGE_BAND_MIN_PX = 4;

/** The lane's measured box: viewport y of its top edge, its height, and the minute at its top. */
export interface LaneGeometry {
  readonly top: number;
  readonly height: number;
  readonly dayStartMin: number;
}

/** Wall minute under a viewport y, clamped to the lane (a lane of height 0 maps to its top). */
export function minuteAtY(geometry: LaneGeometry, clientY: number): number {
  const fraction =
    geometry.height === 0 ? 0 : Math.min(Math.max((clientY - geometry.top) / geometry.height, 0), 1);
  return geometry.dayStartMin + fraction * MINUTES_PER_DAY;
}

/** Pixel y of a wall minute inside the lane's viewport box. */
export function yOfMin(geometry: LaneGeometry, min: number): number {
  return geometry.top + ((min - geometry.dayStartMin) / MINUTES_PER_DAY) * geometry.height;
}

/**
 * Rendered height of a group's card: the same formula the stylesheet and `isTinyBlock` use
 * (2px shorter than the slot, at least 6px), so the hit tests agree with the `tiny` visuals.
 */
export function cardHeightPx(geometry: LaneGeometry, group: DisplayGroup): number {
  return blockHeightPx(group.startMin, group.endMin, geometry.height);
}

/**
 * Edge classification of a mouse press on a block body (§3.2.2): blocks at least 18px tall
 * expose inner edge bands of max(4px, min(8px, 25% of the height)); shorter unselected blocks
 * are whole-block move targets (their resize goes through the outer zones or the pills).
 */
export function edgeFromBands(
  geometry: LaneGeometry,
  clientY: number,
  group: DisplayGroup,
): ResizeEdge | undefined {
  const top = yOfMin(geometry, group.startMin);
  const bottom = yOfMin(geometry, group.endMin);
  const height = cardHeightPx(geometry, group);
  if (height < MIN_TEXT_BLOCK_PX) return undefined;
  const band = Math.max(EDGE_BAND_MIN_PX, Math.min(EDGE_BAND_MAX_PX, height * 0.25));
  if (clientY - top <= band) return ResizeEdge.Start;
  if (bottom - clientY <= band) return ResizeEdge.End;
  return undefined;
}

export interface OuterResizeTarget {
  readonly group: DisplayGroup;
  readonly edge: ResizeEdge;
}

/**
 * A press on empty space within 4px outside the edge of a short (<18px) block resizes that
 * edge (§3.2.2). No outer zone exists on a side another block touches. Ties between
 * equidistant edges go to the top edge of the lower block.
 */
export function outerResizeTarget(
  geometry: LaneGeometry,
  groups: readonly DisplayGroup[],
  clientY: number,
): OuterResizeTarget | null {
  const candidates: Array<OuterResizeTarget & { distance: number; boundaryMin: number }> = [];
  for (const group of groups) {
    const top = yOfMin(geometry, group.startMin);
    const bottom = yOfMin(geometry, group.endMin);
    if (cardHeightPx(geometry, group) >= MIN_TEXT_BLOCK_PX) continue;
    const touching = (boundary: number, side: ResizeEdge): boolean =>
      groups.some(
        (other) =>
          other.key !== group.key &&
          (side === ResizeEdge.Start ? other.endMin === boundary : other.startMin === boundary),
      );
    if (
      !touching(group.startMin, ResizeEdge.Start) &&
      top - clientY >= 0 &&
      top - clientY <= OUTER_RESIZE_ZONE_PX
    ) {
      candidates.push({
        group,
        edge: ResizeEdge.Start,
        distance: top - clientY,
        boundaryMin: group.startMin,
      });
    }
    if (
      !touching(group.endMin, ResizeEdge.End) &&
      clientY - bottom >= 0 &&
      clientY - bottom <= OUTER_RESIZE_ZONE_PX
    ) {
      candidates.push({
        group,
        edge: ResizeEdge.End,
        distance: clientY - bottom,
        boundaryMin: group.endMin,
      });
    }
  }
  const nearest = [...candidates].sort((a, b) => a.distance - b.distance)[0];
  if (nearest === undefined) return null;
  // Equidistant edges: the lower block's top edge wins (§3.2.2 ownership rule ③).
  const tied = candidates.filter((candidate) => candidate.distance === nearest.distance);
  const lowerTop = tied
    .filter((candidate) => candidate.edge === ResizeEdge.Start)
    .sort((a, b) => b.boundaryMin - a.boundaryMin)[0];
  return lowerTop ?? nearest;
}

/** Member whose edge a resize drags: the one holding that outer boundary of the group. */
export function memberForEdge(group: DisplayGroup, edge: ResizeEdge): LiveTimeBlock | undefined {
  const boundary = edge === ResizeEdge.Start ? group.startMin : group.endMin;
  return (
    group.members.find((member) =>
      edge === ResizeEdge.Start ? member.data.startMin === boundary : member.data.endMin === boundary,
    ) ?? group.members[0]
  );
}
